import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../../ComponnentsUI/common/Button';
import { Input } from '../../ComponnentsUI/common/Input';
import { FormField } from '../../ComponnentsUI/common/FormField';
import { RiShieldLine } from 'react-icons/ri';
import { apiClient } from '../../../helpers/apiHelper';
import { useQueryClient } from '@tanstack/react-query';

const GoogleTwoFactorPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [tokenValue, setTokenValue] = useState<string | null>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get('token');
    if (!token) {
      setError('Missing authentication token.');
      setLoading(false);
      return;
    }
    setTokenValue(token);
    setLoading(false);
  }, [location.search]);

  const handleVerify = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!tokenValue) {
      setError('Missing authentication token.');
      return;
    }
    try {
      await apiClient.post('/auth/google/verify-2fa', {
        token: tokenValue,
        totp_code: totpCode,
      });
      queryClient.invalidateQueries({ queryKey: ['auth', 'profile'] });
      navigate('/home');
    } catch (err) {
      if (axios.isAxiosError(err) && err.response) {
        setError(JSON.stringify(err.response.data));
      } else {
        setError('Invalid authentication code.');
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h2 className="text-xl font-semibold mb-4">Preparing 2FA...</h2>
        </div>
      </div>
    );
  }

  if (error && !tokenValue) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="bg-red-50 border border-red-200 rounded-md p-6 max-w-md">
          <h2 className="text-xl font-semibold text-red-700 mb-4">Authentication Error</h2>
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => navigate('/login')} variant="primary">
            Return to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex justify-center items-center p-4 bg-card-color-200 dark:bg-gray-900">
      <div className="w-full max-w-md bg-white p-8 sm:p-10 rounded-2xl shadow-lg bg-card-color-100 dark:bg-gray-800">
        <div className="space-y-6">
          <div className="text-center mb-6">
            <div className="mx-auto w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
              <RiShieldLine className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <h3 className="text-lg font-semibold text-title-page-color-100 dark:text-white mb-2">
              Two-Factor Authentication
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Enter the authentication code to continue.
            </p>
          </div>

          <form onSubmit={handleVerify} className="space-y-4">
            <FormField label="Authentication Code" htmlFor="totp">
              <Input
                id="totp"
                type="text"
                value={totpCode}
                onChange={(e) => setTotpCode(e.target.value)}
                maxLength={6}
                minLength={6}
                placeholder="Enter 6-digit code"
                autoFocus
                className="text-center text-lg tracking-wider bg-gray-50 dark:bg-gray-700 border-gray-200 dark:border-gray-600 focus:bg-white dark:focus:bg-gray-600"
              />
            </FormField>

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                <p className="text-sm text-red-600 dark:text-red-400 text-center">
                  {error}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={totpCode.length !== 6}
              >
                Verify Code
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Back to Login
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default GoogleTwoFactorPage;
